import { useCallback } from 'react';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useCacheActions } from '@atlassian/jira-business-list-handlers/src/controllers/list-cache/main.tsx';
import type { AGGCompliantAPI } from '@atlassian/jira-business-list-handlers/src/controllers/pagination/types.tsx';
import { useHierarchyStore } from '../hierarchy/hierarchy-store/index.tsx';
import { LIST_VIEW_QUERY } from '../../services/get-list-view-issues/gql.tsx';
import type {
	JWMListViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node as JWMListItemNode,
	JWMListViewItems_jira as JWMListViewItems,
} from '../../services/get-list-view-issues/__generated_apollo__/JWMListViewItems';

export const useUpdateCommentsCellCache = () => {
	const { updateItemsInCache } = useCacheActions();
	const [{ issuesMap }] = useHierarchyStore();

	const updateCommentsCache = useCallback(
		(issueKey: string, updatedValue = false) => {
			// early return if it fails the experiment check
			if (!expVal('jira_unread_comments_in_list_view', 'isEnabled', false)) {
				return;
			}

			const issueInCache = [...issuesMap.values()].find((issue) => issue.key === issueKey);

			// early return if no cached issue
			if (!issueInCache) {
				return;
			}

			updateItemsInCache<JWMListItemNode, AGGCompliantAPI<JWMListViewItems>>({
				query: LIST_VIEW_QUERY,
				issueIds: [issueInCache.issueId],
				variables: {
					...issueInCache.cacheVariables,
					isUnreadCommentsEnabled: true,
					includeIsVerifiedInTeamFragment: fg('is-verified-in-business-team-field-fragment'),
				},
				updateItem: (item) => {
					return {
						...item,
						...(item.commentSummary
							? {
									commentSummary: {
										...item.commentSummary,
										hasUnreadComment: updatedValue,
									},
								}
							: {}),
					};
				},
			});
		},
		[issuesMap, updateItemsInCache],
	);

	return { updateCommentsCache };
};
